import type { DocumentSigningRoleType } from '@dealstack/shared/lib/generated';
import { DEFAULT_DATE_BOX_WIDTH_PT } from '@dealstack/shared/lib/common/business/stampGeometry';
import { signatureSlotKey } from '@dealstack/shared/lib/common/business/signatureSlotKey';
import type { SignatureSlotTuple } from '@dealstack/shared/lib/common/business/signatureSlotKey';
import type { CanvasPlacement, PdfBox, SlotMeta } from './PlacementCanvas/PlacementCanvas.types.ts';
import { ASPECT_RATIO_TOLERANCE, expectedAspectRatio } from './PlacementCanvas/placement.utils.ts';
import type { SlotListItem } from './SlotList/SlotList.types.ts';

// Pure, state-free helpers shared by the two placement controllers. Each keeps
// its own state model: the saved-template hook merges server rows + local
// overrides + tombstones (no query→state sync effect); the pending hook owns one map.

// Default stamp width (PDF points) for a freshly-dropped slot. ~2× the min so
// metadata text stays readable; the admin can drag down to the floor.
export const DEFAULT_STAMP_WIDTH_PT = 140;

// Default box for a freshly-dropped slot; height tracks showMetadata and whether the slot
// is a witness (whose box is taller, to fit the address rows) so it matches the renderer.
// offset de-stacks slots. y=600 (bottom-left origin) sits in the top third of
// US-Letter (~792pt) and A4 (~842pt).
export const defaultBoxFor = (showMetadata: boolean, offset: number, isWitness = false): PdfBox => {
	const ratio = expectedAspectRatio(showMetadata, isWitness);
	return {
		x: 50 + offset,
		y: 600 - offset,
		width: DEFAULT_STAMP_WIDTH_PT,
		height: DEFAULT_STAMP_WIDTH_PT / ratio,
	};
};

/** As {@link defaultBoxFor}, at the width a new date field starts at. */
export const defaultDateBoxFor = (showMetadata: boolean, offset: number): PdfBox => ({
	...defaultBoxFor(showMetadata, offset),
	width: DEFAULT_DATE_BOX_WIDTH_PT,
	height: DEFAULT_DATE_BOX_WIDTH_PT / expectedAspectRatio(showMetadata, false),
});

// Rescale a placement to the given ratio (preserve position/width, recompute
// height); used on "Show details" toggle flip or legacy server-row load.
// `y` is a bottom-left origin, so holding it fixed grows the box upward — the Signed row
// and ID footer stay on the line the admin aimed at.
export const rescalePlacement = (placement: CanvasPlacement, ratio: number): CanvasPlacement => ({
	pageNum: placement.pageNum,
	pdfBox: {
		x: placement.pdfBox.x,
		y: placement.pdfBox.y,
		width: placement.pdfBox.width,
		height: placement.pdfBox.width / ratio,
	},
});

/** True when a placement's ratio no longer matches what its slot kind requires — the
 *  condition that makes a legacy server row (or a "Show details" flip) need a rescale. */
export const needsRescale = (placement: CanvasPlacement, ratio: number): boolean => {
	if (placement.pdfBox.height <= 0) {
		return true;
	}
	const currentRatio = placement.pdfBox.width / placement.pdfBox.height;
	return Math.abs(currentRatio - ratio) > ASPECT_RATIO_TOLERANCE;
};

// Build the key→SlotMeta map the canvas reads. keyOf differs by surface: saved
// templates use the signatureSlotKey() tuple, pre-create slots their own client-side key.
// Constrained structurally (not to SlotMeta) because the API slot rows have no `kind`.
export const buildSlotMetaMap = <
	T extends { label: string; isWitness: boolean; roleType: DocumentSigningRoleType },
>(
	slots: T[],
	keyOf: (slot: T) => string
): Map<string, SlotMeta> => {
	const map = new Map<string, SlotMeta>();
	slots.forEach((slot) => {
		map.set(keyOf(slot), {
			label: slot.label,
			isWitness: slot.isWitness,
			roleType: slot.roleType,
			kind: 'signature',
		});
	});
	return map;
};

/** 1-based label for a date box, shared by the canvas meta and the slot list. */
export const dateSlotLabel = (index: number): string => `Date #${index + 1}`;

/** SlotMeta for each date box, labelled 1-based for the author. */
export const buildDateMetaMap = (datePlacementKeys: string[]): Map<string, SlotMeta> => {
	const map = new Map<string, SlotMeta>();
	datePlacementKeys.forEach((key, index) => {
		map.set(key, {
			label: dateSlotLabel(index),
			isWitness: false,
			kind: 'date',
		});
	});
	return map;
};

// Slot-list rows for either saved-template placement editor: signature slots first, then the date
// boxes, which are placed by construction. Pass an empty `slots` to hide the signature rows.
export const buildPlacementSlotListItems = ({
	slots,
	placementsByKey,
	datePlacementKeys,
}: {
	slots: Array<SignatureSlotTuple & { label: string }>;
	placementsByKey: Map<string, CanvasPlacement>;
	datePlacementKeys: string[];
}): SlotListItem[] => [
	...slots.map((slot) => {
		const key = signatureSlotKey(slot);
		return { key, label: slot.label, isPlaced: placementsByKey.has(key) };
	}),
	...datePlacementKeys.map((key, index) => ({
		key,
		label: dateSlotLabel(index),
		isPlaced: true,
	})),
];

/** True when there's at least one slot and every slot has a placement. */
export const isPlacementSetComplete = <T>(
	slots: T[],
	keyOf: (slot: T) => string,
	placementsByKey: Map<string, CanvasPlacement>
): boolean => slots.length > 0 && slots.every((slot) => placementsByKey.has(keyOf(slot)));
